<script>
  import { page } from '@inertiajs/svelte';
  import HomeLayout from '@/components/HomeLayout/index.svelte';
  import Button from '@/components/Button.svelte';

  let { notice = '' } = $props();

  const currentUser = $derived($page?.props?.current_user || null);
</script>

<HomeLayout>
  <div class="home-content">
    {#if notice}
      <p class="notice">{notice}</p>
    {/if}
    
    <div class="hero-section">
      <div class="hero-bg"></div>
      <div class="hero-overlay"></div>
      <div class="hero-content">
        <h1 class="hero-title">Organize your home inventory with Maset</h1>
        <p class="hero-subtitle">Track your possessions, organize containers, and manage borrowing with our easy-to-use platform.</p>
        <div class="cta-buttons">
          {#if currentUser}
            <a href="/dashboard"><Button variant="black">Go to Dashboard</Button></a>
          {:else}
            <a href="/sign_up"><Button variant="black">Get Started</Button></a>
            <a href="/sign_in"><Button variant="outline">Sign In</Button></a>
          {/if}
        </div>
      </div>
    </div>
    
    <div class="features-section">
      <h2 class="section-title">Key Features</h2>
      
      <div class="features-grid">
        <div class="feature-card">
          <div class="feature-icon">📸</div>
          <h3>Visual Inventory</h3>
          <p>Take photos of your items and organize them visually</p>
        </div>
        
        <div class="feature-card">
          <div class="feature-icon">📦</div>
          <h3>Container Management</h3>
          <p>Track what's in each box, drawer, or storage unit</p>
        </div>
        
        <div class="feature-card">
          <div class="feature-icon">🏠</div>
          <h3>Multiple Houses</h3>
          <p>Manage inventory across different properties</p>
        </div>
        
        <div class="feature-card">
          <div class="feature-icon">👥</div>
          <h3>Shared Access</h3>
          <p>Invite family members to collaborate on your inventory</p>
          <span class="coming-soon">Coming soon</span>
        </div>
      </div>
    </div>
  </div>
</HomeLayout>

<style>
  .home-content {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;
    box-shadow: none;
  }
  
  .notice {
    background-color: #ecfdf5;
    color: #065f46;
    padding: 1rem;
    border-radius: 0.375rem;
    margin-bottom: 1.5rem;
  }
  
  .hero-section {
    position: relative;
    text-align: center;
    min-height: 420px;
    max-width: 100vw;
    margin: 3rem auto 2rem auto;
    border-radius: 1.5rem;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .hero-bg {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: url('/images/home_dashboard.jpg') center center/cover no-repeat;
    z-index: 1;
    width: 100%;
    height: 100%;
    filter: brightness(0.7) blur(1px);
    transition: filter 0.3s;
  }
  .hero-overlay {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: linear-gradient(120deg, rgba(2, 44, 34, 0.75) 0%, rgba(13, 148, 136, 0.15) 100%);
    z-index: 2;
    transition: backdrop-filter 0.4s, background 0.4s;
  }
  .hero-section:hover .hero-overlay {
    backdrop-filter: blur(6px) saturate(110%);
    background: rgba(255,255,255,0.18);
  }
  .hero-content {
    position: relative;
    z-index: 3;
    width: 100%;
    max-width: 700px;
    margin: 0 auto;
    padding: 3rem 1rem;
    color: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .hero-title {
    font-size: 3rem;
    font-weight: 800;
    color: #fff;
    margin-bottom: 1.5rem;
    line-height: 1.1;
    text-shadow: 0 2px 16px rgba(0,0,0,0.18);
  }
  .hero-subtitle {
    font-size: 1.25rem;
    color: #e0e7ef;
    margin-bottom: 2.5rem;
    line-height: 1.5;
    text-shadow: 0 1px 8px rgba(0,0,0,0.12);
  }
  .cta-buttons {
    display: flex;
    gap: 1rem;
    justify-content: center;
    margin-bottom: 0;
  }
  .features-section {
    padding: 3rem 0;
  }
  
  .section-title {
    text-align: center;
    font-size: 2rem;
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 3rem;
  }
  
  .features-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 2rem;
  }
  
  .feature-card {
    background-color: white;
    border-radius: 0.5rem;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
    padding: 2rem;
    text-align: center;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  
  .feature-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
  }
  
  .feature-icon {
    font-size: 2.5rem;
    margin-bottom: 1rem;
  }
  
  .feature-card h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 0.75rem;
  }
  
  .feature-card p {
    color: #6b7280;
    line-height: 1.5;
  }
  
  @media (max-width: 768px) {
    .hero-title {
      font-size: 2.5rem;
    }
    .hero-content {
      padding: 2rem 0.5rem;
    }
    .hero-section {
      min-height: 300px;
    }
  }
  
  @media (max-width: 640px) {
    .hero-title {
      font-size: 2rem;
    }
    .hero-section {
      border-radius: 0.75rem;
      min-height: 180px;
    }
  }
  
  .coming-soon {
    display: inline-block;
    margin-top: 0.75rem;
    padding: 0.2em 0.8em;
    font-size: 0.85em;
    font-weight: 600;
    color: #fff;
    background: #065f46;
    border-radius: 999px;
    letter-spacing: 0.04em;
    box-shadow: 0 2px 8px rgba(6,95,70,0.10);
  }
</style>